import { useQuery } from 'react-query';
import { userStore } from '@/store/users';
import { useSearchParams } from 'react-router-dom';
import {
  axiosClient,
  checkIfUserAdmin,
  checkIfUserDeveloper,
  checkIfUserPublisher,
  getUserAccessByPortal,
} from '@/services/utilities';
import { tableStore } from '@/store/table';
import { useEffect } from 'react';
import { frontConfig } from '@/config/front-config';

const { superAdminUsers } = frontConfig(); // ambil super admin dari env/config

export const useUserQuery = () => {
  const [searchParam] = useSearchParams();
  const page = searchParam.get('page') || 1;
  const findUsers = searchParam.get('findUsers') || '';
  const searchBy = searchParam.get('searchBy') || 'username'; // ✅ ambil tipe pencarian
  const sort = searchParam.get('sort') || 'created_at:desc'; // default sort by newest
  const rawSize = Number(searchParam.get('size'));
  const size = rawSize && rawSize > 0 ? Math.min(rawSize, 50) : 10;

  const [users, setUsers, total, setTotal, userLogedIn] = userStore(
    (state: any) => [
      state.users,
      state.setUsers,
      state.totalUsers,
      state.setTotalUsers,
      state.userLogedIn,
    ],
  );

  const setTotalData = tableStore((state) => state.setTotalPages);
  const setOptions = tableStore((state) => state.setOptions);

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const {
        data: { data, total },
      } = await axiosClient.get(
        `/users?page=${page}&size=${size}&${searchBy}=${findUsers}&sort=${sort}`, // ✅ flexible search + sort
      );

      const mappedUser = await Promise.all(
        data.map(async (user: any) => {
          const { username } = user;
          const isUserAdmin = await checkIfUserAdmin(user.username);
          const isUserPublisher = await checkIfUserPublisher(user.username);
          const isUserDeveloper = await checkIfUserDeveloper(user.username);

          let userActivedPortal:
            | 'admin-portal'
            | 'publisher-portal'
            | 'developer-portal' = isUserAdmin
            ? 'admin-portal'
            : isUserPublisher
              ? 'publisher-portal'
              : 'developer-portal';

          const userRole = await getUserAccessByPortal(
            userActivedPortal,
            username,
          );
          const userRolePublisher = await getUserAccessByPortal(
            'publisher-portal',
            username,
          );
          const userRoleAdmin = await getUserAccessByPortal(
            'admin-portal',
            username,
          );
          const userRoleDeveloper = await getUserAccessByPortal(
            'developer-portal',
            username,
          );

          return {
            ...user,
            isAdmin: isUserAdmin,
            isPublisher: isUserPublisher,
            isDeveloper: isUserDeveloper,
            isDeveloperActive: userRoleDeveloper[0]?.active ?? false,
            isPublisherActive: userRolePublisher[0]?.active ?? false,
            isAdminActive: userRoleAdmin[0]?.active ?? false,
            isAllowed: userRole[0]?.allow ?? false,
            isActive: userRole[0]?.active ?? false,
          };
        }),
      );

      // const returnedUser = mappedUser.filter(
      //   (user) =>
      //     !superAdminUsers.includes(user.username) &&
      //     user.username !== userLogedIn?.username
      // );

      return {
        data: mappedUser,
        total,
      };
    },
    onSuccess: ({ data, total }) => {
      setUsers(data);
      setTotal(total);
      setTotalData(total);
      setOptions(total);
    },
    // Disable auto-refresh behaviors
    refetchOnWindowFocus: false, // Disable refetch when switching tabs
    refetchOnMount: false, // Disable auto refetch on mount
    staleTime: 5 * 60 * 1000, // 5 minutes stale time
    cacheTime: 10 * 60 * 1000, // 10 minutes cache time
  });

  useEffect(() => {
    refetch();
  }, [page, size, findUsers, searchBy, sort, refetch]); // ✅ tambahkan `searchBy` dan `sort`

  return { users, isLoading, isFetching, total, refetch };
};
